import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Icon } from '../models/common-class';

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  // Cache para memoização de Object URLs - evita recriar URLs a cada change detection
  private imageUrlCache = new Map<string, string>();
  // Cache para imagens de cliente default (iniciais) - evita recálculo no template
  private defaultImageCache = new Map<string, string>();

  // Timestamp de sessão para forçar a renovação de imagens cacheadas no browser
  private readonly sessionCacheBuster = new Date().getTime().toString();

  constructor(private sanitizer: DomSanitizer) {}

  componentToHex(c) {
    var hex = c.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
  }

  rgbToHex(r, g, b) {
    return '#' + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
  }

  hexToRgb(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  hexToRgbRed(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return parseInt(result[1], 16) || 0;
  }

  hexToRgbGreen(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return parseInt(result[2], 16) || 0;
  }

  hexToRgbBlue(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return parseInt(result[3], 16) || 0;
  }

  transformWhiteToTransparent(base64Image: string): Promise<string> {
    return new Promise<string>(resolve => {
      const image = new Image();
      image.src = base64Image;

      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Função para verificar se um pixel é branco
        function isWhitePixel(x: number, y: number): boolean {
          const index = (y * canvas.width + x) * 4;
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const alpha = pixels[index + 3];
          return red === 255 && green === 255 && blue === 255 && alpha === 255; // Cor branca e opacidade completa
        }

        // Percorre todos os pixels da imagem e torna os pixels brancos transparentes
        for (let x = 0; x < canvas.width; x++) {
          for (let y = 0; y < canvas.height; y++) {
            if (isWhitePixel(x, y)) {
              const index = (y * canvas.width + x) * 4;
              pixels[index + 3] = 0; // Define o canal alfa para 0 (transparente)
            }
          }
        }

        ctx.putImageData(imageData, 0, 0);

        // Converte o canvas resultante de volta para uma imagem base64
        const imageWithTransparentWhite = canvas.toDataURL('image/png');
        resolve(imageWithTransparentWhite);
      };
    });
  }

  removeBlackBorder(base64Image: string, borderColor: string): Promise<string> {
    return new Promise<string>(resolve => {
      const image = new Image();
      image.src = base64Image;

      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Largura e altura da imagem
        const width = canvas.width;
        const height = canvas.height;

        // Função para verificar se um pixel está na borda preta com a cor específica
        function isColoredBorderPixel(x: number, y: number): boolean {
          const index = (y * width + x) * 4;
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const alpha = pixels[index + 3];
          return (
            red === parseInt(borderColor.substring(1, 3), 16) &&
            green === parseInt(borderColor.substring(3, 5), 16) &&
            blue === parseInt(borderColor.substring(5, 7), 16) &&
            alpha === 255
          ); // Cor da borda e opacidade completa
        }

        // Percorre a borda e torna os pixels da borda com a cor específica transparentes
        for (let x = 0; x < width; x++) {
          for (let y = 0; y < height; y++) {
            if (x === 0 || x === width - 1 || y === 0 || y === height - 1) {
              if (isColoredBorderPixel(x, y)) {
                const index = (y * width + x) * 4;
                pixels[index + 3] = 0; // Tornar o pixel transparente
              }
            }
          }
        }

        ctx.putImageData(imageData, 0, 0);

        // Converta o canvas resultante de volta para uma imagem base64
        const imageWithoutBlackBorder = canvas.toDataURL('image/png');
        resolve(imageWithoutBlackBorder);
      };
    });
  }

  isImageFile(file: File): boolean {
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'ico', 'avif'];

    // Obtém a extensão do nome do arquivo
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    // Verifica se a extensão está presente e se está na lista de extensões permitidas
    if (fileExtension && allowedExtensions.includes(fileExtension)) {
      return true;
    }

    return false;
  }

  async resizeImage(file: File, targetSizeKB: number): Promise<File> {
    const targetBytes = targetSizeKB * 1024;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D não disponível.');

    const canvasToBlob = (type: string, quality?: number): Promise<Blob> =>
      new Promise((resolve, reject) => {
        canvas.toBlob(
          b => (b ? resolve(b) : reject(new Error('Falha ao gerar Blob a partir do canvas.'))),
          type,
          quality,
        );
      });

    const loadImageElement = (f: File): Promise<HTMLImageElement> =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(f);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Não foi possível carregar a imagem.'));
        };
        img.src = url;
      });

    // Tenta usar createImageBitmap (rápido); fallback para <img>
    let bitmap: ImageBitmap | null = null;
    let srcW = 0;
    let srcH = 0;

    try {
      bitmap = await createImageBitmap(file);
      srcW = bitmap.width;
      srcH = bitmap.height;
    } catch {
      const img = await loadImageElement(file);
      srcW = img.naturalWidth || img.width;
      srcH = img.naturalHeight || img.height;
    }

    // Detetar se "pode" precisar de transparência:
    // Heurística segura: se for PNG/WebP, assume que pode haver alpha.
    const mayNeedTransparency = file.type === 'image/png' || file.type === 'image/webp';

    // Detetar suporte a WebP (para manter transparência + compressão por quality)
    const supportsWebP = (() => {
      try {
        const test = document.createElement('canvas');
        return test.toDataURL('image/webp').startsWith('data:image/webp');
      } catch {
        return false;
      }
    })();

    // Definir formato de saída
    // - Se pode ter transparência e WebP existe: webp
    // - Se pode ter transparência e WebP não existe: png
    // - Caso contrário: jpeg
    const outputType = mayNeedTransparency ? (supportsWebP ? 'image/webp' : 'image/png') : 'image/jpeg';

    const outputExt = outputType === 'image/webp' ? 'webp' : outputType === 'image/png' ? 'png' : 'jpg';

    // Limites para evitar imagens gigantes
    const maxDimension = 1600;
    let w = srcW;
    let h = srcH;

    const largest = Math.max(w, h);
    if (largest > maxDimension) {
      const scale = maxDimension / largest;
      w = Math.round(w * scale);
      h = Math.round(h * scale);
    }

    const draw = async (dw: number, dh: number) => {
      canvas.width = dw;
      canvas.height = dh;

      // Importante:
      // - Para transparência: não pintar fundo (deixa alpha)
      // - Para JPEG: pinta fundo branco para evitar “preto” em áreas transparentes
      ctx.clearRect(0, 0, dw, dh);
      if (outputType === 'image/jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, dw, dh);
      }

      if (bitmap) {
        ctx.drawImage(bitmap, 0, 0, dw, dh);
      } else {
        const img = await loadImageElement(file);
        ctx.drawImage(img, 0, 0, dw, dh);
      }
    };

    // Estratégia de compressão:
    // - JPEG/WebP: mexe em quality e depois reduz dimensões
    // - PNG: não há quality útil -> reduz dimensões diretamente
    let quality = outputType === 'image/webp' || outputType === 'image/jpeg' ? 0.9 : undefined;

    for (let attempt = 0; attempt < 16; attempt++) {
      await draw(w, h);

      const blob =
        outputType === 'image/png'
          ? await canvasToBlob(outputType) // PNG ignora quality
          : await canvasToBlob(outputType, quality);

      if (blob.size <= targetBytes) {
        if (bitmap) bitmap.close();

        const baseName = file.name.replace(/\.[^/.]+$/, '');
        return new File([blob], `${baseName}.${outputExt}`, {
          type: outputType,
          lastModified: Date.now(),
        });
      }

      // Ajustes para tentar chegar ao target
      if (outputType === 'image/png') {
        // PNG: só dá para reduzir dimensões
        w = Math.max(200, Math.round(w * 0.85));
        h = Math.max(200, Math.round(h * 0.85));
      } else {
        // WebP/JPEG: baixa quality até um limite, depois reduz dimensões
        if ((quality ?? 0) > 0.55) {
          quality = (quality ?? 0.9) - 0.1;
        } else {
          w = Math.max(200, Math.round(w * 0.85));
          h = Math.max(200, Math.round(h * 0.85));
          quality = 0.85;
        }
      }

      if (w <= 200 || h <= 200) break;
    }

    if (bitmap) bitmap.close();
    throw new Error('Não foi possível redimensionar a imagem para o tamanho desejado.');
  }

  resizeImagebk(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      let image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        let width = image.width;
        let height = image.height;

        if (width <= maxWidth && height <= maxHeight) {
          resolve(file);
        }

        let newWidth;
        let newHeight;

        if (width > height) {
          newHeight = height * (maxWidth / width);
          newWidth = maxWidth;
        } else {
          newWidth = width * (maxHeight / height);
          newHeight = maxHeight;
        }

        let canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        let context = canvas.getContext('2d');

        context.drawImage(image, 0, 0, newWidth, newHeight);

        canvas.toBlob(resolve, file.type);
      };
      image.onerror = reject;
    });
  }

  detectMimeType(data: Uint8Array): string {
    if (data.length < 4) return '';
    const header = data.slice(0, 4);
    const hex = Array.from(header)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

    if (hex.startsWith('89504E47')) return 'image/png';
    if (hex.startsWith('FFD8')) return 'image/jpeg';
    if (hex.startsWith('47494638')) return 'image/gif';
    if (hex.startsWith('424D')) return 'image/bmp';
    // WEBP detection could be improved but start with basic Riff
    if (hex.startsWith('52494646')) return 'image/webp';

    return ''; // let browser sniff
  }

  ConvertImageToURL(value: Icon | string | null | any): SafeUrl | string | null {
    try {
      if (!value) return null;

      let imageSource: any = value;

      // Debugging: Log input type (remove in production)
      // console.log('ConvertImageToURL Input:', typeof value, value);

      // 1. Caso seja um Blob ou File diretamente
      if (imageSource instanceof Blob || imageSource instanceof File) {
        return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(imageSource));
      }

      // 2. Se for um objeto que contém 'data'
      if (imageSource && typeof imageSource === 'object' && 'data' in imageSource) {
        if (
          Array.isArray(imageSource.data) ||
          imageSource.data instanceof Uint8Array ||
          typeof imageSource.data === 'string'
        ) {
          imageSource = imageSource.data;
        }
      }

      // 3. Caso String
      if (typeof imageSource === 'string') {
        const strVal = imageSource as string;

        if (strVal.includes('/api/v1/uploads/download/')) {
          const splitToken = '/api/v1/uploads/download/';
          const parts = strVal.split(splitToken);
          if (parts.length === 2) {
            let pathParam = parts[1];

            // Remove previous query parameters from the backend if it doesn't already have path=
            if (!pathParam.startsWith('?')) {
              pathParam = `?path=${pathParam}`;
            }

            // Append session cache buster to force recent images across sessions
            const cacheBuster = `&cb=${this.sessionCacheBuster}`;
            if (!pathParam.includes('&cb=')) {
              pathParam += cacheBuster;
            }

            const baseUrl = parts[0] + splitToken.slice(0, -1);
            imageSource = `${baseUrl}${pathParam}`;
          }
        }

        if (strVal.startsWith('data:') || strVal.startsWith('http') || strVal.startsWith('/')) {
          return this.sanitizer.bypassSecurityTrustUrl(strVal);
        }

        if (strVal.length > 0 && strVal.length < 500 && strVal.includes('/')) {
          return this.sanitizer.bypassSecurityTrustUrl('/' + strVal);
        }

        if (strVal.length > 30 && !strVal.includes(' ') && !strVal.includes('{')) {
          const prefix = strVal.startsWith('iVBORw')
            ? 'data:image/png;base64,'
            : strVal.startsWith('/9j/')
              ? 'data:image/jpeg;base64,'
              : 'data:image/png;base64,';
          return this.sanitizer.bypassSecurityTrustUrl(prefix + strVal);
        }

        try {
          const parsed = JSON.parse(strVal);
          if (parsed && (parsed.data || Array.isArray(parsed))) {
            return this.ConvertImageToURL(parsed);
          }
        } catch (e) {}

        return this.sanitizer.bypassSecurityTrustUrl(strVal);
      }

      // 4. Caso Binário
      let dataItems: any = null;
      if (
        Array.isArray(imageSource) ||
        imageSource instanceof Uint8Array ||
        (imageSource && typeof imageSource === 'object' && imageSource.type === 'Buffer')
      ) {
        dataItems = imageSource.data || imageSource;
      }

      if (dataItems && dataItems.length > 0) {
        const sliceData = Array.from(dataItems.slice(0, 100));
        const cacheKey = `${dataItems.length}_${JSON.stringify(sliceData)}`;

        if (this.imageUrlCache.has(cacheKey)) {
          return this.sanitizer.bypassSecurityTrustUrl(this.imageUrlCache.get(cacheKey)!);
        }

        const uint8Data = new Uint8Array(dataItems as any);
        const mimeType = this.detectMimeType(uint8Data) || 'image/png'; // Fallback to png if unknown, assuming transparency

        // Debug
        // console.log('Detected MIME:', mimeType, 'Length:', uint8Data.length, 'Header:', uint8Data.slice(0, 4));

        const blob = new Blob([uint8Data], { type: mimeType });
        const imageUrl = URL.createObjectURL(blob);
        this.imageUrlCache.set(cacheKey, imageUrl);

        return this.sanitizer.bypassSecurityTrustUrl(imageUrl);
      }
    } catch (e) {
      console.error('Erro ao converter imagem em ImageService:', e);
      return null;
    }
    return null;
  }

  /**
   * Gera um logótipo branco a partir do Icon normal, aplicando
   * os filtros CSS (grayscale → brightness(0) → invert) via canvas.
   * Devolve um Icon pronto a gravar na BD como IconWhite.
   */
  async generateWhiteIcon(icon: Icon | string | null | any): Promise<Icon | null> {
    if (!icon) return null;
    try {
      // Converter o buffer para uma blob URL temporária
      let imageUrl: string | null = null;
      if (typeof icon === 'object' && 'data' in icon && Array.isArray(icon.data)) {
        const uint8Data = new Uint8Array(icon.data);
        const mimeType = this.detectMimeType(uint8Data) || 'image/png';
        const blob = new Blob([uint8Data], { type: mimeType });
        imageUrl = URL.createObjectURL(blob);
      } else if (typeof icon === 'string') {
        imageUrl = icon;
      }
      if (!imageUrl) return null;

      return await new Promise<Icon | null>(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const W = img.naturalWidth || img.width || 400;
          const H = img.naturalHeight || img.height || 120;
          const canvas = document.createElement('canvas');
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) {
            resolve(null);
            return;
          }

          // Desenhar sem filtros para aceder aos pixels reais
          ctx.drawImage(img, 0, 0, W, H);

          // ── Amostrar cor de fundo pelos 4 cantos + centro das bordas ──
          const samplePoints = [
            [0, 0],
            [W - 1, 0],
            [0, H - 1],
            [W - 1, H - 1],
            [Math.floor(W / 2), 0],
            [Math.floor(W / 2), H - 1],
            [0, Math.floor(H / 2)],
            [W - 1, Math.floor(H / 2)],
          ];
          let rSum = 0,
            gSum = 0,
            bSum = 0,
            count = 0;
          for (const [sx, sy] of samplePoints) {
            const d = ctx.getImageData(sx, sy, 1, 1).data;
            if (d[3] > 200) {
              // só amostras opacas
              rSum += d[0];
              gSum += d[1];
              bSum += d[2];
              count++;
            }
          }
          // Cor de fundo detectada (ou branco por defeito)
          const bgR = count ? Math.round(rSum / count) : 255;
          const bgG = count ? Math.round(gSum / count) : 255;
          const bgB = count ? Math.round(bSum / count) : 255;

          // ── Processar todos os pixels ──
          const imageData = ctx.getImageData(0, 0, W, H);
          const d = imageData.data;
          const TOLERANCE = 40; // distância máxima ao fundo para considerar "fundo"

          for (let i = 0; i < d.length; i += 4) {
            const r = d[i],
              g = d[i + 1],
              b = d[i + 2],
              a = d[i + 3];

            // Pixel já transparente — manter
            if (a < 30) continue;

            // Distância euclidiana ao fundo detectado
            const dist = Math.sqrt(Math.pow(r - bgR, 2) + Math.pow(g - bgG, 2) + Math.pow(b - bgB, 2));

            if (dist < TOLERANCE) {
              // Cor similar ao fundo → tornar transparente
              d[i + 3] = 0;
            } else {
              // Conteúdo real → tornar branco puro
              d[i] = 255;
              d[i + 1] = 255;
              d[i + 2] = 255;
              d[i + 3] = 255;
            }
          }

          ctx.putImageData(imageData, 0, 0);

          // Revogar blob URL temporária
          if (imageUrl!.startsWith('blob:')) URL.revokeObjectURL(imageUrl!);

          canvas.toBlob(async blob => {
            if (!blob) {
              resolve(null);
              return;
            }
            const file = new File([blob], 'icon-white.png', { type: 'image/png' });
            const buffer = await this.FileToBuffer(file);
            resolve(buffer);
          }, 'image/png');
        };
        img.onerror = () => {
          if (imageUrl!.startsWith('blob:')) URL.revokeObjectURL(imageUrl!);
          resolve(null);
        };
        img.src = imageUrl!;
      });
    } catch (e) {
      console.error('Erro ao gerar logo branco:', e);
      return null;
    }
  }

  /**
   * Limpa o cache de URLs de imagens e liberta memória.
   * Útil para chamar no logout ou quando necessário libertar recursos.
   */
  clearImageCache(): void {
    this.imageUrlCache.forEach(url => URL.revokeObjectURL(url));
    this.imageUrlCache.clear();
  }

  /**
   * Invalida o cache de uma imagem específica.
   * Deve ser chamado quando a imagem é atualizada para forçar a recriação do URL.
   */
  invalidateImageCache(Icon?: Icon | string | null): void {
    if (Icon && typeof Icon === 'object' && 'data' in Icon && (Icon as any).data) {
      const cacheKey = JSON.stringify((Icon as any).data.slice(0, 100));
      const existingUrl = this.imageUrlCache.get(cacheKey);
      if (existingUrl) {
        try {
          URL.revokeObjectURL(existingUrl);
        } catch (_) {}
        this.imageUrlCache.delete(cacheKey);
      }
    } else {
      this.imageUrlCache.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
      this.imageUrlCache.clear();
    }
  }

  async convertBase64ToBlob(file: File): Promise<Blob> {
    let base64Data;
    base64Data = await this.ConvertToBase64(file);
    // const base64 = await fetch(base64Data);
    const base64Response = await fetch(base64Data);
    return await base64Response.blob();
  }

  base64ToArrayBuffer(base64: any): ArrayBuffer {
    var binary_string = window.atob(base64);
    var len = binary_string.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
  }

  async blobToBase64Async(blob: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = _e => reject(fileReader.error);
      fileReader.onloadend = _e => {
        const dataUrl = fileReader.result as string;
        // remove "data:mime/type;base64," prefix from data url
        const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        resolve(base64);
      };
      fileReader.readAsDataURL(blob);
    });
  }

  convertDataUrlToBlob(dataUrl): Blob {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  ConvertToBuffer(fileData) {
    return function (resolve) {
      try {
        var reader = new FileReader();
        reader.readAsArrayBuffer(fileData);
        reader.onload = function () {
          var arrayBuffer = reader.result;
          var bytes = new Uint8Array(arrayBuffer as ArrayBuffer);
          resolve([].slice.call(bytes));
        };
      } catch (error) {}
    };
  }

  async FileToBufferbk(file: File) {
    const fileData = new Blob([file]);

    type TypeBlob = {
      type: 'Buffer'; // ou type: string, se tiver outras opções além de Buffer
      data: number[];
    };

    let ficheiro: TypeBlob;
    // tslint:disable-next-line: prefer-const
    let promise = new Promise(this.ConvertToBuffer(fileData));
    // Wait for promise to be resolved, or log error.
    // tslint:disable-next-line: only-arrow-functions
    await promise
      .then(function (dataresult) {
        ficheiro = {
          type: 'Buffer',
          data: dataresult as number[],
        };

        // tslint:disable-next-line: only-arrow-functions
      })
      .catch(function (err) {
        console.log('Error: ', err);
      });

    return ficheiro;
  }

  async FileToBuffer(file: File) {
    try {
      const fileData = await file.arrayBuffer();

      const bufferData = new Uint8Array(fileData);
      const bufferArray: number[] = Array.from(bufferData);

      const ficheiro: Icon = {
        type: 'Buffer',
        data: bufferArray,
      };

      return ficheiro;
    } catch (error) {
      return null;
    }
  }

  criarImagemClienteDefault(background: string, nomeCompleto: string, cordonome: string, diametro: number): string {
    // Cache key baseada nos parâmetros
    const cacheKey = `${background}|${nomeCompleto}|${cordonome}|${diametro}`;

    // Retornar do cache se existir
    if (this.defaultImageCache.has(cacheKey)) {
      return this.defaultImageCache.get(cacheKey)!;
    }

    // Extrai as iniciais do primeiro e último nomes do nome completo
    if (!nomeCompleto) {
      nomeCompleto = '?';
    }
    const partesNome = nomeCompleto.trim().split(/\s+/);
    const primeiraLetraPrimeiroNome = partesNome[0][0];
    const primeiraLetraUltimoNome = partesNome.length > 1 ? partesNome[partesNome.length - 1][0] : '';

    // Usa as iniciais para criar a imagem
    const iniciais = primeiraLetraPrimeiroNome.toUpperCase() + primeiraLetraUltimoNome.toUpperCase();

    const canvas = document.createElement('canvas');
    canvas.width = diametro;
    canvas.height = diametro;
    const ctx = canvas.getContext('2d');

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = diametro / 2;

    // Define as cores de fundo e de texto padrão
    let backgroundColor = background;
    let textColor = cordonome;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Desenha o círculo com as novas cores
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    ctx.fillStyle = textColor;
    ctx.font = 'bold ' + diametro / 2 + 'px Indie Flower';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(iniciais, centerX, centerY);

    const imagemURL = canvas.toDataURL();

    // Guardar no cache
    this.defaultImageCache.set(cacheKey, imagemURL);

    return imagemURL;
  }

  criarImagemClienteDefaultbk(
    cor1: string,
    cor2: string,
    cor3: string,
    nomeCompleto: string,
    cordonome: string,
    diametro: number,
  ): string {
    // Extrai as iniciais do primeiro e último nomes do nome completo
    if (!nomeCompleto) {
      nomeCompleto = '?';
    }
    const partesNome = nomeCompleto.trim().split(/\s+/);
    const primeiraLetraPrimeiroNome = partesNome[0][0];
    const primeiraLetraUltimoNome = partesNome.length > 1 ? partesNome[partesNome.length - 1][0] : '';

    // Usa as iniciais para criar a imagem
    const iniciais = primeiraLetraPrimeiroNome.toUpperCase() + primeiraLetraUltimoNome.toUpperCase();

    const canvas = document.createElement('canvas');
    canvas.width = diametro;
    canvas.height = diametro;
    const ctx = canvas.getContext('2d');

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = (diametro - 6) / 2;

    const gradient = ctx.createLinearGradient(centerX - radius, centerY, centerX + radius, centerY);
    gradient.addColorStop(0, cor1);
    gradient.addColorStop(0.33, cor2);
    gradient.addColorStop(0.66, cor3);
    gradient.addColorStop(1, cor1);

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.lineWidth = 3;
    ctx.strokeStyle = gradient;
    ctx.stroke();

    ctx.fillStyle = cordonome;
    ctx.font = 'bold ' + diametro / 2 + 'px Indie Flower';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(iniciais, centerX, centerY);

    const imagemURL = canvas.toDataURL();
    return imagemURL;
  }

  ConvertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  base64ToBlob(base64Data: string, contentType: string): Blob {
    const sliceSize = 512;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }
}
